import { Asset } from "@storytell/cp-client";
import { dispatchGlobalEvent } from "@storytell/events";
import type { CitationNodeProps } from "@storytell/rich-text";
import type { AssetId } from "@storytell/types";
import { StTooltip, StTooltipTrigger } from "@storytell/ui/tooltip";
import { BsChatSquareQuoteFill } from "solid-icons/bs";
import { TbFileText, TbQuote, TbX } from "solid-icons/tb";
import { type Component, Show } from "solid-js";
import { twMerge } from "tailwind-merge";
import { useWire } from "~/wire";

export const ArtifactCitationRenderer: Component<CitationNodeProps> = (props) => {
  const wire = useWire();

  // using 26 as that is the length of an asset_{20 chars here} id
  // after the id there's an underscore followed by a number which is the
  // citation number
  const parsed = () => {
    return {
      assetId: props.node.attrs.value.substring(0, 26) as AssetId,
      chunkNum: props.node.attrs.value.substring(27),
    };
  };
  const asset = () => wire.assets.get(parsed().assetId);
  const isDeleted = () => asset()?.status.state === "DELETED";

  const truncatedFilename = () => {
    const maxLength = 40;
    const filename = asset()?.filename || "";
    if (filename.length <= maxLength) return isDeleted() ? `(Deleted) ${filename}` : filename;

    const extIndex = filename.lastIndexOf(".");
    const extension = extIndex !== -1 ? filename.slice(extIndex) : "";
    const nameWithoutExt = extIndex !== -1 ? filename.slice(0, extIndex) : filename;

    const charsToShow = maxLength - extension.length - 3; // 3 for "..."
    if (charsToShow <= 0) return isDeleted() ? `(Deleted) ${filename}` : filename;

    const frontChars = Math.ceil(charsToShow / 2);
    const backChars = Math.floor(charsToShow / 2);

    const truncated = `${nameWithoutExt.slice(0, frontChars)}...${nameWithoutExt.slice(nameWithoutExt.length - backChars)}${extension}`;
    return isDeleted() ? `(Deleted) ${truncated}` : truncated;
  };

  return (
    <Show when={asset()}>
      <StTooltip
        placement="top"
        content={
          <div class="px-1 py-1 pb-2">
            <p class="font-medium text-xs mb-1">Citation</p>
            <p class="text-sm mb-2">Content generated based on data from</p>

            <button
              type="button"
              onClick={() => {
                dispatchGlobalEvent("open-asset-in-pane", { assetId: parsed().assetId });
              }}
              class={twMerge(
                "group/mention cursor-pointer inline-flex items-center gap-1 bg-asset-bg rounded-lg text-sm text-asset-text hover:bg-asset-bg-hover border px-1 py-2 border-asset-border",
                props.selected && "ring-2 ring-asset-border",
                isDeleted() && "line-through pointer-events-none",
              )}
            >
              <span class="transition-transform text-asset-icon cursor-pointer">
                <TbFileText class="size-3.5" />
              </span>
              <span>{truncatedFilename()}</span>
            </button>
          </div>
        }
      >
        <StTooltipTrigger
          as="span"
          class="inline-block relative ml-1 -top-0.5 text-muted-text"
          data-citation={props.node.attrs.value}
        >
          <BsChatSquareQuoteFill class="inline size-4" />
        </StTooltipTrigger>
      </StTooltip>
    </Show>
  );
};
